import { useCallback, useEffect, useRef, useState } from 'react';

// Section-by-section behaviour for the home page:
//  - one wheel/trackpad gesture moves exactly one section (desktop, 981px+ wide)
//  - the side rail (previous / dots / next) and the active link in the top bar follow the page
//  - in-page links scroll smoothly instead of jumping
//  - the mobile menu opens and closes
// Native snapping (CSS) handles the keyboard and the scrollbar; it is switched on only
// while this page is mounted, via the "lp-html" class on <html>.

const BAR_HEIGHT = 76;
const STEPPING_QUERY = '(min-width: 981px) and (min-height: 600px) and (max-height: 2400px)';

const log = (...args) => console.log('[LandingPage]', ...args);

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function useLandingScroll() {
  const rootRef = useRef(null);
  const [cur, setCur] = useState(0);
  const [items, setItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const wheel = useRef({ busyUntil: 0, last: 0 });

  const sections = useCallback(
    () => (rootRef.current ? Array.from(rootRef.current.querySelectorAll('[data-snap]')) : []),
    []
  );

  const smoothTo = useCallback((target) => {
    const behavior = reducedMotion() ? 'auto' : 'smooth';
    const y = target.getBoundingClientRect().top + window.pageYOffset - BAR_HEIGHT;
    window.scrollTo({ top: Math.max(0, y), behavior });
  }, []);

  const currentIndex = useCallback((secs) => {
    let i = 0;
    secs.forEach((s, k) => {
      if (s.getBoundingClientRect().top - BAR_HEIGHT <= 4) i = k;
    });
    return i;
  }, []);

  // page-level classes on <html> (snapping, scroll padding) only while the home page is shown
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('lp-html');
    log('home page shown');
    return () => {
      html.classList.remove('lp-html');
      log('home page closed');
    };
  }, []);

  // keep the rail and the top-bar highlight in step with the scroll position
  useEffect(() => {
    let raf = 0;
    const sync = () => {
      raf = 0;
      const secs = sections();
      if (!secs.length) return;
      const line = Math.min(window.innerHeight * 0.45, 420);
      let i = 0;
      secs.forEach((s, k) => {
        if (s.getBoundingClientRect().top - line <= 0) i = k;
      });
      setCur(i);
      setItems((prev) =>
        prev.length === secs.length
          ? prev
          : secs.map((s) => ({ id: s.id, label: s.getAttribute('data-snap') }))
      );
    };
    const tick = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    return () => {
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sections]);

  // one gesture = one section on desktop; tall sections scroll normally until their end
  useEffect(() => {
    const onWheel = (e) => {
      if (e.ctrlKey || Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;
      if (!window.matchMedia(STEPPING_QUERY).matches) return;
      const now = Date.now();
      const w = wheel.current;
      if (w.busyUntil && (now < w.busyUntil || now - w.last < 160)) {
        w.last = now;
        e.preventDefault();
        return;
      }
      w.last = now;
      w.busyUntil = 0;
      const secs = sections();
      if (!secs.length) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      const i = currentIndex(secs);
      const r = secs[i].getBoundingClientRect();
      if (dir > 0 && r.bottom > window.innerHeight + 4) return;
      if (dir < 0 && r.top < BAR_HEIGHT - 4) return;
      const next = i + dir;
      if (next < 0 || next >= secs.length) return;
      e.preventDefault();
      w.busyUntil = now + 850;
      log(`wheel: section ${i + 1} -> ${next + 1} (${secs[next].getAttribute('data-snap')})`);
      smoothTo(secs[next]);
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [sections, currentIndex, smoothTo]);

  const navTo = useCallback(
    (e) => {
      const a = e && e.currentTarget;
      const id = a ? (a.getAttribute('href') || '').replace('#', '') : '';
      const target = id ? document.getElementById(id) : null;
      if (target) {
        e.preventDefault();
        log(`link: scrolling to #${id}`);
        smoothTo(target);
      }
      setMenuOpen(false);
    },
    [smoothTo]
  );

  const go = useCallback(
    (delta) => {
      const secs = sections();
      const i = Math.max(0, Math.min(secs.length - 1, currentIndex(secs) + delta));
      if (secs[i]) {
        log(`rail: going to section ${i + 1} (${secs[i].getAttribute('data-snap')})`);
        smoothTo(secs[i]);
      }
    },
    [sections, currentIndex, smoothTo]
  );

  const label = items[cur] ? items[cur].label : 'Welcome';
  const key =
    label === 'Welcome' ? 'home'
      : label === 'Why LegalApp' ? 'why'
      : label === 'How it works' || /^\d\d ·/.test(label) ? 'how'
      : label === "Who it's for" ? 'who'
      : '';
  const navCls = (k) => (k === key ? 'lp-on' : '');
  const menuCls = (k) => (k === key ? 'lp-menu-link lp-on' : 'lp-menu-link');

  return {
    rootRef,
    navTo,
    dots: items.map((it, i) => ({
      href: '#' + it.id,
      label: it.label,
      cls: i === cur ? 'lp-rail-dot lp-on' : 'lp-rail-dot',
      current: i === cur ? 'true' : 'false',
    })),
    railCls: 'lp-rail',
    atStart: cur <= 0,
    atEnd: items.length > 0 && cur >= items.length - 1,
    goPrev: () => go(-1),
    goNext: () => go(1),
    navHome: navCls('home'),
    navWhy: navCls('why'),
    navHow: navCls('how'),
    navWho: navCls('who'),
    menuHome: menuCls('home'),
    menuWhy: menuCls('why'),
    menuHow: menuCls('how'),
    menuWho: menuCls('who'),
    menuOpen,
    menuClosed: !menuOpen,
    menuExpanded: menuOpen ? 'true' : 'false',
    menuLabel: menuOpen ? 'Close menu' : 'Open menu',
    toggleMenu: () => {
      log(menuOpen ? 'menu closed' : 'menu opened');
      setMenuOpen((o) => !o);
    },
  };
}
